import { ref, reactive, computed } from 'vue';
import { useAsyncState } from '@vueuse/core';
import router from '@dashboardApp/router';
import { legacyApi, jsonapiStore } from '@dashboardApp/store';
import { defineStore, acceptHMRUpdate } from 'pinia';
import { useUserStore } from '@dashboardApp/stores/UserStore';
import { dateTimeAgo } from '@dashboardApp/helpers/date';
import useNotifications from '@dashboardApp/composables/useNotifications';
import { performAnalyticsTracking } from '@dashboardApp/helpers/performAnalyticsTracking';
import * as dayjs from 'dayjs';

const { addAlert } = useNotifications();

export const useSupplierStore = defineStore('SupplierStore', () => {
    const { track: trackingEvent } = performAnalyticsTracking();
    const store = jsonapiStore();
    const supplier = reactive({});
    const userStore = useUserStore();
    const brandsFirstSetLimit = ref(12);
    const productsFirstSetLimit = ref(12);
    const paymentMethodsFirstSetLimit = ref(8);
    const shippingMethodsFirstSetLimit = ref(8);
    const tagsRef = ref(null);
    const notesRef = ref(null);

    // Full address
    const address = computed(() => {
        const parts = [
            supplier.address,
            supplier.city,
            supplier.state,
            ',',
            supplier.country?.name
        ];
        return parts.join(' ');
    });
    const types = computed(() => supplier?.types?.join(', '));

    /**
     * Split brands into 2 sets, the first set to display and the remaining ones
     */
    const brandsSet = computed(() => {
        return [
            supplier.brands?.slice(0, brandsFirstSetLimit.value),
            supplier.brands?.slice(brandsFirstSetLimit.value)
        ];
    });

    /**
     * Split products into 2 sets, the first set to display and the remaining ones
     */
    const productsSet = computed(() => {
        return [
            supplier.products?.slice(0, productsFirstSetLimit.value),
            supplier.products?.slice(productsFirstSetLimit.value)
        ];
    });
    
    /**
     * Split payment methods into 2 sets, the first set to display and the remaining ones
     */
    const paymentMethodsSet = computed(() => {
        return methodsSet(supplier.payment_methods || {}, paymentMethodsFirstSetLimit.value);
    });

    /**
     * Split shipping methods into 2 sets, the first set to display and the remaining ones
     */
    const shippingMethodsSet = computed(() => {
        return methodsSet(supplier.shipping_methods || {}, shippingMethodsFirstSetLimit.value);
    });

    function methodsSet(methods, firstSetLimit = 3) {
        const keys = Object.keys(methods);
        const firstSetKeys = keys.slice(0, firstSetLimit);
        const secondSetKeys = keys.slice(firstSetLimit);

        const filter = (localObj, includeKeys) => {
            return Object.keys(localObj)
                .filter(key => includeKeys.includes(key))
                .reduce((obj, key) => {
                    return {
                        ...obj,
                        [key]: localObj[key]
                    };
                }, {})
        };

        return [
            filter(methods, firstSetKeys),
            filter(methods, secondSetKeys),
        ];
    };

    const isOwner = computed(() => supplier?.member_id === userStore.user.id);
    const editListingUrl = computed(() => {
        if (userStore.user.admin) {
            return `/admin/suppliers/edit/${supplier._jv?.id}`;
        }
        if (isOwner.value) {
            return '/suppliers/edit';
        }
    });
    const canEdit = computed(() => isOwner.value || userStore.user.admin);
    const hasCatalog = computed(() => supplier.has_csv_catalog || supplier.has_pdf_catalog);
    const catalogLastUpdated = computed(() => {
        if (supplier.catalogue_last_updated) {
            return dateTimeAgo(dayjs.tz(supplier.catalogue_last_updated, 'UTC'));
        } 
        return null
    });
    const documentsVerified = computed(() => supplier.business_registration && supplier.merchandise_license);

    function avatar(size) {
        return supplier.avatars?.[size];
    }

    const { execute: getSupplier, isReady: supplierIsReady, isLoading: supplierIsLoading } = useAsyncState(
        getSupplierPromise,
        {},
        { immediate: false }
    );

    function getSupplierPromise(args) {
        const slug = args?.slug || null;
        const params = args?.params || {};

        return store.get([`suppliers/${slug}`, { params }]).then((res) => {
            Object.assign(supplier, res);
        }).catch((errs) => {
            if (errs.response.status === 404) {
                router.replace({ name: 'shone-dashboard' });
                addAlert({ message: 'Invalid supplier.', type: 'error' });
            }

            if (process.env.NODE_ENV === 'development') {
                console.log(errs)
            }
        });
    }

    function verifyWebsite(slug) {
        return legacyApi.get(`/suppliers/verify_website/${slug}`).then(({ data }) => {
            if (data.status === 'ok') {
                supplier.website_verified = true;
            }
            return data;
        });
    }

    function getWebsiteVerificationCode(slug) {
        return legacyApi.get(`/suppliers/get_website_verification_code/${slug}`).then(({ data }) => {
            return data;
        });
    }

    function verifyEmail(slug) {
        return legacyApi.get(`/suppliers/setup_email_verification/${slug}`).then(({ data }) => {
            return data;
        });
    }

    function flag(formData) {
        return legacyApi.post('/suppliers/flag_listing', { data: formData }).then(({ data }) => {
            return data;
        });
    }

    return {
        supplier,
        address, 
        types, 
        brandsFirstSetLimit,
        productsFirstSetLimit,
        paymentMethodsFirstSetLimit,
        shippingMethodsFirstSetLimit,
        tagsRef,
        notesRef,
        brandsSet, 
        productsSet,
        paymentMethodsSet, 
        shippingMethodsSet, 
        isOwner,
        editListingUrl, 
        canEdit, 
        hasCatalog, 
        catalogLastUpdated,
        documentsVerified,
        avatar,
        getSupplier, 
        supplierIsReady,
        supplierIsLoading,
        verifyWebsite, 
        getWebsiteVerificationCode, 
        verifyEmail, 
        flag
    }
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useSupplierStore, import.meta.hot));
}
